/**
 * Audit ZA — Auth-Helper für Supabase Email/Password.
 *
 * Phase 5-Setup: Single-User → Multi-User.
 * Nutzt Supabase-Auth (auth.signInWithPassword, signUp, signOut).
 * Session wird im Client persistiert (createClient persistSession: true).
 *
 * RLS-Policies sind in dieser Welle NOCH NICHT aktiviert. Die Auth-Schicht
 * dient zunächst dem UI-Schutz (Login-Page → ProtectedRoute) und der
 * Vorbereitung der Daten-Lokalität via auth.uid().
 *
 * In Welle ZB werden RLS-Policies aktiviert; ab dann ist die Auth-Schicht
 * auch DB-seitig wirksam.
 */

import type { Session, User } from '@supabase/supabase-js';

import { supabase } from './supabase';

export type { Session, User };

export interface AuthResult {
  session: Session | null;
  error: string | null;
}

/**
 * Aktuelle Session synchron lesen — null wenn nicht eingeloggt.
 * Nützlich für currentUserId(), das ohne async laufen muss.
 */
let cachedSession: Session | null = null;

export function getCachedSession(): Session | null {
  return cachedSession;
}

export function setCachedSession(s: Session | null): void {
  cachedSession = s;
}

/**
 * Initialisiert das Session-Cache aus Supabase. Wird beim App-Start aufgerufen.
 */
export async function initAuth(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  cachedSession = data.session;
  return data.session;
}

export async function signIn(email: string, password: string): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { session: null, error: error.message };
  cachedSession = data.session;
  return { session: data.session, error: null };
}

export async function signUp(email: string, password: string): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) return { session: null, error: error.message };
  cachedSession = data.session;
  return { session: data.session, error: null };
}

/**
 * Magic-Link senden — User klickt Link in E-Mail, ist automatisch eingeloggt.
 * Kein Passwort nötig. Gut für „Passwort vergessen" oder Komfort-Login.
 */
export async function sendMagicLink(email: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/`,
    },
  });
  if (error) return { error: error.message };
  return { error: null };
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
  cachedSession = null;
}

/**
 * Listener-Wrapper für Session-Änderungen (Login, Logout, Token-Refresh).
 * Liefert die unsubscribe-Funktion zurück.
 */
export function onAuthChange(callback: (s: Session | null) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    cachedSession = session;
    callback(session);
  });
  return () => data.subscription.unsubscribe();
}
